import type { PropsWithChildren } from 'react'

import {
  FvButton,
  FvLinkButton,
  LoadMore,
  OrDivider,
  TextField,
} from '@fv/client-components'

import InfoBox from '@/components/InfoBox'
import { InputAdornment } from '@/components/inputs/InputGroup'
import { ShipperSlider } from '@/components/ShipperSlider'

import { HasPermission } from '../auth'
import { Product } from './Product'
import { ProductCatalogForm } from './ProductCatalogForm'
import { useProducts } from './useHandlingUnits'
import { type ProductCatalogInit, useProductCatalog } from './useProductCatalog'

export const ProductCatalogListSearch = ({ children }: PropsWithChildren) => {
  const productCatalog = useProductCatalog()
  return (
    <div className="flex gap-x-3 items-center">
      <div className="input-group flex-1">
        <InputAdornment
          icon="search"
          iconClass="text-fv-green"
          position="start"
        />
        <TextField
          autoFocus
          className="form-control"
          name="productSearch"
          onChange={e => productCatalog.search(e.target.value)}
          placeholder="i.e. Widgets"
          value={productCatalog.searchText}
        />
      </div>
      <HasPermission name="manage-products">
        <OrDivider />
        <FvButton icon="plus" theme="default" onClick={productCatalog.showForm}>
          Add product
        </FvButton>
      </HasPermission>
      {children}
    </div>
  )
}

type ListProps = {
  inline?: boolean
}
export const ProductCatalogList = ({ inline }: ListProps) => {
  const productCatalog = useProductCatalog()
  const productsQuery = useProducts(
    productCatalog.isOpen || !!inline,
    productCatalog.filter,
    productCatalog.searchText,
  )
  return (
    <>
      {!productsQuery.data.length &&
        (productsQuery.isLoading ? (
          <InfoBox icon="spinner">Loading...</InfoBox>
        ) : (
          <InfoBox>No products found.</InfoBox>
        ))}

      {productsQuery.data.map(p => (
        <Product key={p._id} product={p} />
      ))}

      <LoadMore
        className="mt-4"
        fetchNextPage={productsQuery.fetchNextPage}
        hasNextPage={productsQuery.hasNextPage}
        isLoading={productsQuery.isFetchingNextPage}
      />
    </>
  )
}

export const ProductCatalogPanelContents = () => {
  const productCatalog = useProductCatalog()
  const productsQuery = useProducts(
    productCatalog.isOpen,
    productCatalog.filter,
    productCatalog.searchText,
  )

  const editingProduct = productsQuery.data.find(
    p => p._id === productCatalog.editingId,
  )
  return (
    <>
      {productCatalog.view === 'list' && (
        <div className="p-6">
          <div className="sticky top-0 z-10 -mx-6 -mt-6 border-b bg-white p-6 py-4">
            <ProductCatalogListSearch />
          </div>

          <div className="mt-8">
            <ProductCatalogList />
          </div>
        </div>
      )}

      {productCatalog.view === 'form' && (
        <ProductCatalogForm
          initialValues={editingProduct}
          mode={productCatalog.filter.mode}
        />
      )}
    </>
  )
}
export const ProductCatalog = () => {
  const close = useProductCatalog(s => s.close)
  const isOpen = useProductCatalog(s => s.isOpen)
  return (
    <ShipperSlider onClose={close} isOpen={isOpen} flush>
      <ProductCatalogPanelContents />
    </ShipperSlider>
  )
}
type Props = {
  className?: string
  disabled?: boolean
  init: ProductCatalogInit
  variant?: 'button' | 'icon'
  label?: string
  onClick?: () => void
}

export const ProductCatalogButton = ({
  disabled,
  onClick,
  init,
  variant = 'button',
  label = 'Product catalog',
}: Props) => {
  const openProductCatalog = useProductCatalog(s => s.open)
  const handleClick = () => onClick ?? openProductCatalog(init)

  if (variant === 'icon') {
    return (
      <FvLinkButton
        aria-label="Open product catalog"
        disabled={disabled}
        icon="box"
        onClick={handleClick}
        title={label}
      />
    )
  }

  return (
    <FvButton
      disabled={disabled}
      theme="default"
      icon="forklift"
      onClick={handleClick}
    >
      {label}
    </FvButton>
  )
}
